/**
 * The bridge message set (PRD `agentic-app-direct-bridge` §8.3, FR-1, D7) — the page's half.
 *
 * What travels inside `OuterEnvelope.payload` between an app page and its Talkie host. A CLOSED set:
 * every message carries a `t` discriminator; the page sends only {@link AppToHost} and accepts only
 * {@link HostToApp}. Anything else is refused and counted before it can reach a handler. Results and
 * reads carry MCP *data types* (`CallToolResult`, `ReadResourceResult`); nothing here is JSON-RPC.
 *
 * Host counterpart: `platform/crates/agentic-app-host/src/bridge/protocol.rs`. Field names and field
 * ORDER match its serde declaration order, so a message the page builds encodes to the same bytes the
 * host's own encoder produces (pinned by the cross-language vectors).
 */

export const BRIDGE_VERSION = 1 as const;

/** Largest encoded message either side may send (D7). */
export const MAX_MESSAGE_BYTES = 4 * 1024 * 1024;
/** Largest encoded `hello` (D7). */
export const MAX_HELLO_BYTES = 64 * 1024;
/** Most tool handlers one `hello` may name (D7). */
export const MAX_HANDLERS = 64;
/** Most resource URIs/templates one `hello` may declare (D7). */
export const MAX_RESOURCES = 16;
/** Longest id, instance, URI or app version the host accepts from a page. */
export const MAX_TOKEN_LEN = 512;
/** Longest bridge error code or hello handler name, in ASCII characters. */
export const MAX_CODE_LEN = 128;

/** A bridge code/name is admitted exactly as sent: no trimming, normalization or closed vocabulary. */
export const isBridgeCode = (v: unknown): v is string =>
  typeof v === "string" && v.length > 0 && v.length <= MAX_CODE_LEN && !/[^A-Za-z0-9_.-]/.test(v);

/** An MCP data type (`CallToolResult` / `ReadResourceResult`), opaque to the library. */
export type McpResult = Record<string, unknown>;

/** A protocol fault: `code` is 1–128 ASCII characters from `[A-Za-z0-9_.-]` in either direction. */
export interface BridgeError {
  readonly code: string;
  readonly message: string;
}

export type RequestKind = "grant" | "connect" | "open_link";

// ── app → host

export interface Hello {
  readonly t: "hello";
  readonly v: typeof BRIDGE_VERSION;
  /** Fresh per page load: lets the host tell a reload from a repeated hello (FR-13). */
  readonly instance: string;
  readonly handlers: readonly string[];
  readonly resources: readonly string[];
  readonly app_version: string;
}
export interface ResultMsg { readonly t: "result"; readonly gen: number; readonly id: string; readonly result: McpResult }
/** A protocol fault answering a `call` or `read` — never an app failure (those are `result` + `isError`). */
export interface ErrorMsg { readonly t: "error"; readonly gen: number; readonly id: string; readonly error: BridgeError }
export interface Pong { readonly t: "pong"; readonly gen: number; readonly id: string }
export interface RequestMsg { readonly t: "request"; readonly gen: number; readonly id: string; readonly kind: RequestKind; readonly params: unknown }
/** App → host cancel: only of a request the page itself started. */
export interface AppCancel { readonly t: "cancel"; readonly gen: number; readonly id: string; readonly reason: string }
export type AppToHost = Hello | ResultMsg | ErrorMsg | Pong | RequestMsg | AppCancel;

/**
 * Every message the page sends is built here, field by field in `protocol.rs`'s declaration order, so the
 * page's bytes are the host's own (`test/contract/bridge.vectors.json` holds both to them) and no message
 * carries a field the host's `deny_unknown_fields` decoder would refuse.
 */
export const appMessage = {
  hello: (instance: string, handlers: readonly string[], resources: readonly string[], appVersion: string): Hello => ({
    t: "hello",
    v: BRIDGE_VERSION,
    instance,
    handlers: [...handlers],
    resources: [...resources],
    app_version: appVersion,
  }),
  result: (gen: number, id: string, result: McpResult): ResultMsg => ({ t: "result", gen, id, result }),
  error: (gen: number, id: string, code: string, message: string): ErrorMsg => ({ t: "error", gen, id, error: { code, message } }),
  pong: (gen: number, id: string): Pong => ({ t: "pong", gen, id }),
  request: (gen: number, id: string, kind: RequestKind, params: unknown): RequestMsg => ({ t: "request", gen, id, kind, params }),
  cancel: (gen: number, id: string, reason: string): AppCancel => ({ t: "cancel", gen, id, reason }),
} as const;

// ── host → app

/** What this host offers the page (D6). Branch on it, never on a platform name. */
export interface HostCapabilities {
  readonly grants: ReadonlyArray<{ readonly name: string; readonly connect: boolean }>;
  readonly open_link: boolean;
  /** Whether this host renders the app's `ui://` views itself (own-app-views FR-1/FR-7). */
  readonly views: boolean;
}
export type Welcome = {
  readonly t: "welcome";
  readonly v: number;
  /** Echo of `hello.instance`; a welcome for another instance is ignored. */
  readonly instance: string;
} & (
  | { readonly accepted: { readonly generation: number; readonly host: HostCapabilities }; readonly refused?: undefined }
  | { readonly refused: { readonly error: BridgeError }; readonly accepted?: undefined }
);
export interface CallMsg { readonly t: "call"; readonly gen: number; readonly id: string; readonly tool: string; readonly args: unknown; readonly waits_on_person: boolean }
export interface ReadMsg { readonly t: "read"; readonly gen: number; readonly id: string; readonly uri: string }
export interface HostCancel { readonly t: "cancel"; readonly gen: number; readonly id: string; readonly reason: string }
export interface Ping { readonly t: "ping"; readonly gen: number; readonly id: string }
export interface ResponseMsg {
  readonly t: "response";
  readonly gen: number;
  readonly id: string;
  readonly result?: unknown;
  readonly error?: BridgeError;
}
export type HostToApp = Welcome | CallMsg | ReadMsg | HostCancel | Ping | ResponseMsg;

/** Host codes (`welcome.refused`, `response.error`) — `protocol.rs` `codes`. */
export const HOST_CODES = {
  UNSUPPORTED_VERSION: "unsupported_version",
  APP_RELOADED: "app_reloaded",
  UNSUPPORTED: "unsupported",
  INVALID_REQUEST: "invalid_request",
  TOO_MANY_REQUESTS: "too_many_requests",
  CANCELLED: "cancelled",
} as const;

/** Codes the page reports in an `error` (protocol faults only). */
export const PAGE_CODES = {
  UNKNOWN_TOOL: "unknown_tool",
  UNKNOWN_RESOURCE: "unknown_resource",
  STALE_GENERATION: "stale_generation",
  RESOURCE_UNAVAILABLE: "resource_unavailable",
  RESULT_TOO_LARGE: "result_too_large",
} as const;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isGen = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v) && v >= 0;
const isToken = (v: unknown): v is string => typeof v === "string" && v.length > 0 && v.length <= MAX_TOKEN_LEN;
const isError = (v: unknown): v is BridgeError =>
  isObject(v) && hasOnly(v, ["code", "message"]) && isBridgeCode(v.code) && typeof v.message === "string";
/** The host's messages are a closed shape too (`bridge.schema.json`): a field this page does not know is refused. */
const hasOnly = (v: Record<string, unknown>, keys: readonly string[]): boolean => Object.keys(v).every((k) => keys.includes(k));

function isCapabilities(v: unknown): v is HostCapabilities {
  return (
    isObject(v) &&
    hasOnly(v, ["grants", "open_link", "views"]) &&
    Array.isArray(v.grants) &&
    v.grants.every((g) => isObject(g) && hasOnly(g, ["name", "connect"]) && typeof g.name === "string" && typeof g.connect === "boolean") &&
    typeof v.open_link === "boolean" &&
    typeof v.views === "boolean"
  );
}

/** Each host kind's fields (`bridge.schema.json` `$defs`). */
const HOST_FIELDS: Readonly<Record<string, readonly string[]>> = {
  welcome: ["t", "v", "instance", "accepted", "refused"],
  call: ["t", "gen", "id", "tool", "args", "waits_on_person"],
  read: ["t", "gen", "id", "uri"],
  cancel: ["t", "gen", "id", "reason"],
  ping: ["t", "gen", "id"],
  response: ["t", "gen", "id", "result", "error"],
};

/** Narrow an untrusted envelope payload to the closed host → app union, or `null` (refused, counted). */
export function parseHostMessage(payload: unknown): HostToApp | null {
  if (!isObject(payload) || typeof payload.t !== "string") return null;
  const m = payload;
  const fields = Object.hasOwn(HOST_FIELDS, payload.t) ? HOST_FIELDS[payload.t] : undefined;
  if (!fields || !hasOnly(m, fields)) return null;
  switch (m.t) {
    case "welcome": {
      if (typeof m.v !== "number" || typeof m.instance !== "string") return null;
      const accepted = m.accepted;
      if (isObject(accepted) && m.refused === undefined) {
        return hasOnly(accepted, ["generation", "host"]) && isGen(accepted.generation) && isCapabilities(accepted.host)
          ? (m as unknown as Welcome)
          : null;
      }
      if (isObject(m.refused) && m.accepted === undefined) {
        return hasOnly(m.refused, ["error"]) && isError(m.refused.error) ? (m as unknown as Welcome) : null;
      }
      return null;
    }
    case "call":
      return isGen(m.gen) && isToken(m.id) && isToken(m.tool) && "args" in m && typeof m.waits_on_person === "boolean"
        ? (m as unknown as CallMsg)
        : null;
    case "read":
      return isGen(m.gen) && isToken(m.id) && isToken(m.uri) ? (m as unknown as ReadMsg) : null;
    case "cancel":
      return isGen(m.gen) && isToken(m.id) && typeof m.reason === "string" ? (m as unknown as HostCancel) : null;
    case "ping":
      return isGen(m.gen) && isToken(m.id) ? (m as unknown as Ping) : null;
    case "response":
      if (!isGen(m.gen) || !isToken(m.id)) return null;
      if (m.error !== undefined) return isError(m.error) && m.result === undefined ? (m as unknown as ResponseMsg) : null;
      return "result" in m ? (m as unknown as ResponseMsg) : null;
    default:
      return null;
  }
}

/** UTF-8 length of `value`'s JSON encoding — what the host's `fits_in` measures. */
export function encodedBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}
